import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { formControlClasses, themeClasses } from '../config/themeClasses';
import { 
  User, 
  Bell, 
  Shield, 
  Database,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      email: user?.email || '',
      role: user?.role || 'USER',
      notifications: true,
      emailAlerts: true,
      riskAlerts: true
    }
  });

  const tabs = [
    { id: 'profile', nameKey: 'settingsTabProfile', icon: User },
    { id: 'notifications', nameKey: 'settingsTabNotifications', icon: Bell },
    { id: 'security', nameKey: 'settingsTabSecurity', icon: Shield },
    { id: 'data', nameKey: 'settingsTabData', icon: Database },
  ];

  const onSubmit = async () => {
    toast.error(t('settingsSaveNotAvailable'));
  };

  const handleLogout = () => {
    logout();
    toast.success(t('settingsLoggedOutSuccess'));
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <Card className={themeClasses.appKit.sectionShell}>
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">{t('settingsTitle')}</h1>
            <p className="text-text-secondary text-base sm:text-lg">{t('settingsDescription')}</p>
          </div>
          <div className="hidden lg:flex shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-info-500/10 rounded-full flex items-center justify-center border-2 border-info-500/30">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-info-500" aria-hidden />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center rounded-sm border-l-[3px] px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 bg-primary-50 text-text-primary'
                      : 'border-transparent text-text-secondary hover:bg-canvas-soft-2 hover:text-text-primary'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {t(tab.nameKey)}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <Card className={themeClasses.appKit.sectionShell}>
            <CardContent className="p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-4">
                      Profile Information
                    </h3>
                    
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="firstname" className="block text-sm font-medium text-text-primary mb-2">
                          First Name
                        </label>
                        <input
                          {...register('firstname', { required: 'First name is required' })}
                          type="text"
                          className={formControlClasses.input}
                        />
                        {errors.firstname && (
                          <p className="mt-1 text-sm text-danger-500">{errors.firstname.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="lastname" className="block text-sm font-medium text-text-primary mb-2">
                          Last Name
                        </label>
                        <input
                          {...register('lastname', { required: 'Last name is required' })}
                          type="text"
                          className={formControlClasses.input}
                        />
                        {errors.lastname && (
                          <p className="mt-1 text-sm text-danger-500">{errors.lastname.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                        Email Address
                      </label>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                        type="email"
                        className={formControlClasses.input}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-danger-500">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="mt-6">
                      <label htmlFor="role" className="block text-sm font-medium text-text-primary mb-2">
                        Role
                      </label>
                      <select {...register('role')} className={formControlClasses.input}>
                        <option value="USER">User</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <Button type="submit" disabled={loading}>
                        {loading ? (
                          <div className="flex items-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            {t('settingsSaving')}
                          </div>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            {t('settingsSaveChanges')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-4">
                      Notification Preferences
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-text-primary">
                            Email Notifications
                          </h4>
                          <p className="text-sm text-text-secondary">
                            Receive email notifications for important updates
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            {...register('notifications')}
                            type="checkbox"
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-background-tertiary peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-light after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-text-primary">
                            Email Alerts
                          </h4>
                          <p className="text-sm text-text-secondary">
                            Get notified when companies are added to your watchlist
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            {...register('emailAlerts')}
                            type="checkbox"
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-background-tertiary peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-light after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-text-primary">
                            Risk Alerts
                          </h4>
                          <p className="text-sm text-text-secondary">
                            Receive alerts when risk levels change for watched companies
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            {...register('riskAlerts')}
                            type="checkbox"
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-background-tertiary peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-light after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-4">
                      Security Settings
                    </h3>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-medium text-text-primary mb-2">
                          Change Password
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-text-primary mb-2">
                              Current Password
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className={`${formControlClasses.input} pr-10`}
                                placeholder={t('settingsCurrentPasswordPlaceholder')}
                              />
                              <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-5 w-5 text-text-tertiary" />
                                ) : (
                                  <Eye className="h-5 w-5 text-text-tertiary" />
                                )}
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary mb-2">
                              New Password
                            </label>
                            <input
                              type="password"
                              className={formControlClasses.input}
                              placeholder={t('settingsNewPasswordPlaceholder')}
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              className={formControlClasses.input}
                              placeholder={t('settingsConfirmPasswordPlaceholder')}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-border-light">
                        <h4 className="text-sm font-medium text-text-primary mb-2">
                          Account Actions
                        </h4>
                        <div className="space-y-3">
                          <Button type="button" variant="outline">
                            {t('settingsDownloadData')}
                          </Button>
                          <Button type="button" variant="destructive" onClick={handleLogout}>
                            {t('sidebarSignOut')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Data & Privacy Tab */}
              {activeTab === 'data' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-4">
                      Data & Privacy
                    </h3>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-medium text-text-primary mb-2">
                          Data Export
                        </h4>
                        <p className="text-sm text-text-secondary mb-4">
                          Download a copy of your data including watchlist, addresses, and routes.
                        </p>
                        <Button type="button" variant="outline">
                          {t('settingsExportData')}
                        </Button>
                      </div>

                      <div className="pt-4 border-t border-border-light">
                        <h4 className="text-sm font-medium text-text-primary mb-2">
                          Data Deletion
                        </h4>
                        <p className="text-sm text-text-secondary mb-4">
                          Permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                        <Button type="button" variant="destructive">
                          {t('settingsDeleteAccount')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
